import { apiService } from 'core/services/apiService';
import { paginationService } from 'core/services/paginationService';
import { Partner, CommonPartner } from 'types';

/**
 * Partner Type from /partner_types endpoint
 */
export interface PartnerType {
	'@context': string;
	'@id': string;
	'@type': string;
	id: number;
	label: string;
}

/**
 * Get partner_lists parameters
 */
export interface GetPartnersParams {
	limit?: number;
	page?: number;
	active?: boolean;
}

/**
 * Raw partner_lists response from API
 */
export interface RawPartnerListResponse {
	'@context': string;
	'@id': string;
	'@type': string;
	id: number;
	label: string;
	partnerId: number;
	type?: string;
	parameters?: any[];
	workplaces?: string[];
	active: boolean;
	deleted: boolean;
	created_at: string;
	updated_at: string;
}

/**
 * Get partner_lists response structure
 */
export interface GetPartnersResponse {
	member?: RawPartnerListResponse[];
	items?: RawPartnerListResponse[];
	total?: number;
	totalCount?: number;
}

/**
 * Create partner_list payload
 */
export interface CreatePartnerPayload {
	label: string;
	partnerId: number;
	type: string;
	parameters?: any[];
	workplaces?: string[];
	active: boolean;
	deleted?: boolean;
}

/**
 * Update partner_list payload
 */
export interface UpdatePartnerPayload {
	label: string;
	partnerId: number;
	type: string;
	parameters?: any[];
	workplaces?: string[];
	active: boolean;
	deleted?: boolean;
	created_at?: string;
	updated_at?: string;
}

/**
 * Transform raw API response to Partner type
 */
const transformPartnerListResponse = (raw: RawPartnerListResponse): Partner => {
	return {
		'@context': raw['@context'],
		'@id': raw['@id'],
		'@type': raw['@type'],
		id: raw.id,
		label: raw.label,
		partnerId: raw.partnerId,
		type: raw.type,
		parameters: raw.parameters || [],
		workplaces: raw.workplaces || [],
		active: raw.active,
		deleted: raw.deleted,
		created_at: raw.created_at,
		updated_at: raw.updated_at,
	};
};

/**
 * Get all common partners from /common/partners
 */
export const getCommonPartners = async (): Promise<CommonPartner[]> => {
	try {
		const endpoint = '/common/partners';
		
		const response = await paginationService.getWithHandler(
			endpoint,
			undefined,
			100 // Get more items as common partners should be a relatively small list
		);

		const partnerList: CommonPartner[] = response?.member || response?.items || [];

		if (Array.isArray(partnerList)) {
			return partnerList;
		}

		return [];
	} catch (error: any) {
		console.error('Failed to fetch common partners:', error);
		throw new Error(error.message || 'Failed to fetch common partners');
	}
};

/**
 * Get all partner types from /partner_types
 */
export const getPartnerTypes = async (): Promise<PartnerType[]> => {
	try {
		const endpoint = '/partner_types';
		
		const response = await paginationService.getWithHandler(
			endpoint,
			undefined,
			100 // Get more items as partner types should be a relatively small list
		);

		const partnerTypeList: PartnerType[] = response?.member || response?.items || [];

		if (Array.isArray(partnerTypeList)) {
			return partnerTypeList;
		}

		return [];
	} catch (error: any) {
		console.error('Failed to fetch partner types:', error);
		throw new Error(error.message || 'Failed to fetch partner types');
	}
};

/**
 * Get all partner_lists with optional filtering and pagination
 */
export const getPartners = async (params: GetPartnersParams = {}): Promise<Partner[]> => {
	try {
		const {
			limit = 30,
			active,
		} = params;

		const queryParams: any = {};
		
		if (active !== undefined) {
			queryParams.active = active ? 1 : 0;
		}

		const endpoint = '/partner_lists';

		const queryString = Object.keys(queryParams).length > 0 
			? '?' + new URLSearchParams(queryParams).toString() 
			: '';
		const itemsPerPage = Math.min(limit, 30);
		const response = await paginationService.getWithHandler(
			`${endpoint}${queryString}`,
			undefined,
			itemsPerPage
		);

		const partnerList: RawPartnerListResponse[] = response?.member || response?.items || [];

		if (Array.isArray(partnerList)) {
			return partnerList.map(transformPartnerListResponse);
		}

		return [];
	} catch (error: any) {
		console.error('Failed to fetch partner_lists:', error);
		throw new Error(error.message || 'Failed to fetch partner_lists');
	}
};

/**
 * Get a single partner_list by ID
 */
export const getPartner = async (partnerId: number): Promise<Partner> => {
	try {
		const response = apiService.responseHandler(
			await apiService.get(`/partner_lists/${partnerId}`)
		);

		const rawData: RawPartnerListResponse = response?.data || response;
		return transformPartnerListResponse(rawData);
	} catch (error: any) {
		console.error('Failed to fetch partner_list:', error);
		throw new Error(error.message || 'Failed to fetch partner_list');
	}
};

/**
 * Create a new partner_list
 */
export const createPartner = async (payload: CreatePartnerPayload): Promise<Partner> => {
	try {
		const response = apiService.responseHandler(
			await apiService.post('/partner_lists', payload)
		);

		const rawData: RawPartnerListResponse = response?.data || response;
		return transformPartnerListResponse(rawData);
	} catch (error: any) {
		console.error('Failed to create partner_list:', error);
		throw new Error(error.message || 'Failed to create partner_list');
	}
};

/**
 * Update an existing partner_list
 */
export const updatePartner = async (
	partnerId: number,
	payload: Partial<UpdatePartnerPayload>
): Promise<Partner> => {
	try {
		const response = apiService.responseHandler(
			await apiService.patch(`/partner_lists/${partnerId}`, payload)
		);

		const rawData: RawPartnerListResponse = response?.data || response;
		return transformPartnerListResponse(rawData);
	} catch (error: any) {
		console.error('Failed to update partner_list:', error);
		throw new Error(error.message || 'Failed to update partner_list');
	}
};

/**
 * Delete a partner_list
 */
export const deletePartner = async (partnerId: number): Promise<void> => {
	try {
		await apiService.delete(`/partner_lists/${partnerId}`);
	} catch (error: any) {
		console.error('Failed to delete partner_list:', error);
		throw new Error(error.message || 'Failed to delete partner_list');
	}
};

/**
 * Toggle partner_list active status
 */
export const togglePartnerStatus = async (partnerId: number, active: boolean): Promise<Partner> => {
	try {
		const response = apiService.responseHandler(
			await apiService.patch(`/partner_lists/${partnerId}`, { active })
		);

		const rawData: RawPartnerListResponse = response?.data || response;
		return transformPartnerListResponse(rawData);
	} catch (error: any) {
		console.error('Failed to toggle partner_list status:', error);
		throw new Error(error.message || 'Failed to toggle partner_list status');
	}
};

/**
 * Partner List Workplace interface
 */
export interface PartnerListWorkplace {
	'@context': string;
	'@id': string;
	'@type': string;
	id: number;
	partnerList: string;
	workplace: string;
}

/**
 * Create partner_list_workplace association
 */
export const createPartnerListWorkplace = async (payload: {
	partnerList: string;
	workplace: string;
}): Promise<PartnerListWorkplace> => {
	try {
		const response = apiService.responseHandler(
			await apiService.post('/partner_list_workplaces', payload)
		);
		return response?.data || response;
	} catch (error: any) {
		console.error('Failed to create partner_list_workplace:', error);
		throw new Error(error.message || 'Failed to create partner_list_workplace');
	}
};

/**
 * Get partner_list_workplaces for a partner list
 */
export const getPartnerListWorkplaces = async (params: {
	partnerList?: number;
	workplace?: number;
}): Promise<PartnerListWorkplace[]> => {
	try {
		const queryParams: any = {};
		if (params.partnerList) queryParams.partnerList = params.partnerList;
		if (params.workplace) queryParams.workplace = params.workplace;

		const queryString = Object.keys(queryParams).length > 0
			? '?' + new URLSearchParams(queryParams).toString()
			: '';

		const response = await paginationService.getWithHandler(
			`/partner_list_workplaces${queryString}`,
			undefined,
			100
		);

		const workplaceList: PartnerListWorkplace[] = response?.member || response?.items || [];
		return Array.isArray(workplaceList) ? workplaceList : [];
	} catch (error: any) {
		console.error('Failed to fetch partner_list_workplaces:', error);
		throw new Error(error.message || 'Failed to fetch partner_list_workplaces');
	}
};

/**
 * Delete partner_list_workplace association
 */
export const deletePartnerListWorkplace = async (id: number): Promise<void> => {
	try {
		await apiService.delete(`/partner_list_workplaces/${id}`);
	} catch (error: any) {
		console.error('Failed to delete partner_list_workplace:', error);
		throw new Error(error.message || 'Failed to delete partner_list_workplace');
	}
};

// Export as service object for convenience
export const partnersService = {
	getCommonPartners,
	getPartnerTypes,
	getPartners,
	getPartner,
	createPartner,
	updatePartner,
	deletePartner,
	togglePartnerStatus,
	createPartnerListWorkplace,
	getPartnerListWorkplaces,
	deletePartnerListWorkplace,
};

export default partnersService;

